import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Trophy } from "lucide-react";
import { LevelSeal } from "@/components/LevelSeal";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";

export default function AdminConquistas() {
  const [settings, setSettings] = useState(null);
  const [byLevel, setByLevel] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/level-settings").then(r => setSettings(r.data)).catch(() => {});
    api.get("/admin/users-by-level").then(r => setByLevel(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        hours_recorrente: Number(settings.hours_recorrente),
        hours_especialista: Number(settings.hours_especialista),
        hours_embaixador: Number(settings.hours_embaixador),
        min_courses_especialista: Number(settings.min_courses_especialista),
        enable_consultancy_online: !!settings.enable_consultancy_online,
        enable_consultancy_presencial: !!settings.enable_consultancy_presencial,
        enable_kit_vip: !!settings.enable_kit_vip,
      };
      await api.put("/admin/level-settings", payload);
      toast.success("Configuração salva. Níveis dos alunos foram recalculados.");
      load();
    } catch { toast.error("Erro ao salvar"); }
    finally { setSaving(false); }
  };

  return (
    <AdminLayout title="Conquistas e Níveis">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="rounded-xl"><CardContent className="p-5 space-y-4">
          <h3 className="font-display font-semibold text-[hsl(var(--primary))] flex items-center gap-2"><Trophy className="w-4 h-4" /> Critérios de Níveis</h3>
          {!settings ? <Skeleton className="h-40 w-full" /> : (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Aluno Recorrente (h)</Label><Input type="number" min={1} value={settings.hours_recorrente} onChange={(e) => setSettings(s => ({ ...s, hours_recorrente: e.target.value }))} data-testid="settings-h-recorrente" /></div>
                <div><Label>Especialista CEAP (h)</Label><Input type="number" min={1} value={settings.hours_especialista} onChange={(e) => setSettings(s => ({ ...s, hours_especialista: e.target.value }))} data-testid="settings-h-especialista" /></div>
                <div><Label>Embaixador CEAP (h)</Label><Input type="number" min={1} value={settings.hours_embaixador} onChange={(e) => setSettings(s => ({ ...s, hours_embaixador: e.target.value }))} data-testid="settings-h-embaixador" /></div>
              </div>
              <div><Label>Mínimo de cursos para Especialista</Label><Input type="number" min={1} value={settings.min_courses_especialista} onChange={(e) => setSettings(s => ({ ...s, min_courses_especialista: e.target.value }))} /></div>
              <div className="space-y-2 pt-2 border-t border-[hsl(var(--border))]">
                <p className="text-sm font-medium">Benefícios habilitados</p>
                <div className="flex items-center gap-2"><Switch checked={!!settings.enable_consultancy_online} onCheckedChange={(v) => setSettings(s => ({ ...s, enable_consultancy_online: v }))} /><span className="text-sm">Consultoria Online</span></div>
                <div className="flex items-center gap-2"><Switch checked={!!settings.enable_consultancy_presencial} onCheckedChange={(v) => setSettings(s => ({ ...s, enable_consultancy_presencial: v }))} /><span className="text-sm">Consultoria Presencial</span></div>
                <div className="flex items-center gap-2"><Switch checked={!!settings.enable_kit_vip} onCheckedChange={(v) => setSettings(s => ({ ...s, enable_kit_vip: v }))} /><span className="text-sm">Kit VIP CEAP</span></div>
              </div>
              <Button onClick={save} disabled={saving} className="w-full" data-testid="settings-save">{saving ? "Salvando…" : "Salvar configurações"}</Button>
              <p className="text-[11px] text-muted-foreground">Ao salvar, os níveis de todos os alunos são recalculados conforme novos critérios.</p>
            </div>
          )}
        </CardContent></Card>

        <Card className="rounded-xl"><CardContent className="p-5">
          <h3 className="font-display font-semibold text-[hsl(var(--primary))] mb-3">Distribuição de alunos por nível</h3>
          {byLevel.length === 0 ? <p className="text-sm text-muted-foreground">Sem dados ainda.</p> : (
            <ul className="space-y-2">
              {["Aluno CEAP", "Aluno Recorrente", "Especialista CEAP", "Embaixador CEAP"].map((lvl) => {
                const found = byLevel.find(x => x.level === lvl);
                const count = found?.count || 0;
                const total = byLevel.reduce((acc, x) => acc + (x.count || 0), 0) || 1;
                const pct = Math.round((count / total) * 100);
                const seal = lvl === "Aluno Recorrente" ? "bronze" : lvl === "Especialista CEAP" ? "prata" : lvl === "Embaixador CEAP" ? "ouro" : null;
                return (
                  <li key={lvl} className="border border-[hsl(var(--border))] rounded-lg p-3 flex items-center gap-3">
                    {seal ? <LevelSeal seal={seal} size={32} /> : <div className="w-8 h-8 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center text-[hsl(var(--primary))]">•</div>}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{lvl}</span>
                        <span className="text-xs font-mono-id">{count} aluno{count !== 1 ? "s" : ""}</span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                        <div className="h-full bg-[hsl(var(--accent))]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent></Card>
      </div>
    </AdminLayout>
  );
}
